/**
 * Nav
 *
 * A component to handle the site navigation
 * @see global-options.php to see how we are handling menu data in the global DP object
 * Styles for elements with too much logic are in nav.css
 *
 * Child Components:
 * - NavCloseButton
 * - NavOpenButton
 * - NavSearchbar
 * - NavSubmenu
 */

/**
 * React dependencies
 */

import React, { useEffect, useRef, useState } from 'react';
import { createRoot } from 'react-dom/client';

/**
 * DWT Dependencies
 */

import { closeButtonIcon, mobileMenuIcon } from '../.././img/icons';

/**
 * DWT hooks
 */

import useMediaQuery from '../../hooks/useMediaQuery';

/**
 * DWT components
 */

import NavCloseButton from './NavCloseButton';
import NavOpenButton from './NavOpenButton';
import NavSearchBar from './NavSearchBar';
import NavSubmenu from './NavSubmenu';

const Nav = () => {
  const [ menuData, setMenuData ] = useState( [] );

  const [ isOpenMainMenu, setIsOpenMainMenu ] = useState( false );
  const [ itemID, setItemID ] = useState( null );

  const [ isOpenSearch, setIsOpenSearch ] = useState( false );
  const [ searchText, setSearchText ] = useState( '' );

  const isDesktop = useMediaQuery( '( min-width: 1366px )' );
  const menuRef = useRef( null );

  useEffect( () => {
    setMenuData( window.DP.main_menu_data );
  }, [] );

  /**
   * Handle data
   */

  const mainMenu = menuData?.map( ( item ) => item.menu_item_parent === '0' ? item : null ).filter( ( fitleredItem ) => fitleredItem !== null );
  const allSubmenuItems = menuData?.map( ( item ) => item.menu_item_parent !== '0' ? item : null ).filter( ( fitleredItem ) => fitleredItem !== null );
  mainMenu.filter( ( item, index ) => index <= 4 ? item : null );

  const siteLogo = window.DP.nav_logo;
  const mobileLogo = window.DP.nav_logo_mobile;

  /**
   * Handle mobile main menu
   */

  const handleMobileMenuOpenClose = () => {
    if ( searchText !== '' ) {
      setSearchText( '' );
    }

    setIsOpenMainMenu( !isOpenMainMenu );
  };

  /**
   * Handle submenus
   */

  const hasSubmenuItems = ( mainMenuItem, submenu ) => submenu.find( ( submenuItem ) => parseInt( submenuItem.menu_item_parent ) === mainMenuItem.ID ) !== undefined;
  const getSubmenus = ( mainMenuItem, submenu ) => submenu.filter( ( submenuItem ) => parseInt( submenuItem.menu_item_parent ) === mainMenuItem.ID );
  const handleSubmenu = ( e, item ) => setItemID( item.ID === itemID && !e.target.classList.contains( 'submenu' ) ? null : item.ID );
  const isSubmenuOpen = ( item ) => itemID === item.ID;

  /**
   * Handle utils for menus
   */

  const resetMenus = () => {
    if ( !isDesktop ) {
      setIsOpenMainMenu( false );
      setItemID( null );
    } else {
      setItemID( null );
    }
  };

  const handleClickOffMainMenu = ( e ) => {
    if ( !menuRef?.current.contains( e.target ) ) {
      resetMenus();
    }
  };

  const setMaxMenuLength = ( menu, max ) => {
    if ( menu.length <= max ) {
      return menu;
    }

    return menu.filter( ( item, index ) => index <= max - 1 ? item : null );
  };

  const getItemInReverse = ( menu, position ) => menu[menu?.length - position];

  useEffect( () => {
    document.body.classList.toggle( 'is-open-menu', isOpenMainMenu );
    document.addEventListener( 'mousedown', handleClickOffMainMenu );
    return () => document.removeEventListener( 'mousedown', handleClickOffMainMenu );
  }, [ isOpenMainMenu ] );

  return (
    <nav className={ `site-nav flex justify-center px-20 h-[70px] md:px-50 xl:px-113 ${!isDesktop ? 'is-mobile-nav' : ''}` }>
      <div className='nav--inner w-full flex justify-between items-center xl:max-w-[1280px]'>
        <div className='site-logo-wrapper my-15'>
          <a className='site-logo__link' href='/' data-title='home'>
            <img className='site-logo__image h-[50px] mr-[4rem]' src={ isDesktop ? siteLogo?.url : mobileLogo?.url } alt={ isDesktop ? siteLogo?.alt : mobileLogo?.url } />
          </a>
        </div>
        <NavOpenButton
          openButtonCondition={ !isDesktop && !isOpenMainMenu }
          openButtonClassNames='my-22 nav__btn--open'
          openIcon={ mobileMenuIcon }
          handleOpenClose={ handleMobileMenuOpenClose }
          ariaLabel='Open Menu'
        />
        <NavCloseButton
          closeButtonCondition={ !isDesktop && isOpenMainMenu }
          closeButtonClassNames='my-22 nav__btn--close'
          closeIcon={ closeButtonIcon }
          handleOpenClose={ handleMobileMenuOpenClose }
          ariaLabel='Close Menu'
        />
        <ul
          ref={ menuRef }
          className={ `main-menu mr-0 overflow-hidden ${isDesktop ? 'is-desktop-menu' : 'is-mobile-menu'} ${isOpenMainMenu ? 'is-active' : ''}` }
          role='menubar'
        >
          { mainMenu?.map( ( item, index ) => (
            <li
              key={ item.ID }
              className={`menu-item mb-0 ${itemID === item.ID ? 'is-active' : ''} ${item.url ? 'has-link' : 'has-button'} ${hasSubmenuItems( item, allSubmenuItems ) ? 'has-dropdown' : ''}` }
              data-item-index={ index }
              aria-haspopup={ !!hasSubmenuItems( item, allSubmenuItems ) }
              aria-expanded={ itemID === item.ID }
              aria-label={ item.title }
              role='menuitem'
              onClick={ ( e ) => !isDesktop && hasSubmenuItems( item, allSubmenuItems ) ? handleSubmenu( e, item ) : null }
              onFocus={ () => hasSubmenuItems( item, allSubmenuItems ) ? setItemID( item.ID ) : setItemID( null ) }
              onBlur={ () => setItemID( null ) }
              onKeyDown={ e => e.key === 'Escape' ? setItemID( null ) : null }
            >
              { ( item.url && item.url !== '#' && !hasSubmenuItems( item, allSubmenuItems ) || ( item.url && item.url !== '#' && isDesktop ) ) && (
                <a
                  className='menu-item__link w-full whitespace-nowrap no-underline'
                  href={ item.url }
                  target={ item.target ? '_blank' : null }
                  rel={ item.target ? 'noopener noreferrer' : null }
                  role='link'
                >
                  <span dangerouslySetInnerHTML={ { __html: item.title } } />
                </a>
              ) }
              { ( !item.url || item.url === '#' || ( hasSubmenuItems( item, allSubmenuItems ) && !isDesktop ) ) && (
                <button
                  className="menu-item__button w-full text-left whitespace-nowrap no-underline"
                  role='menuitem'
                >
                  <span dangerouslySetInnerHTML={ { __html: item.title } } />
                </button>
              ) }
              <NavSubmenu
                mainMenu={ mainMenu }
                item={ item }
                allSubmenuItems={ allSubmenuItems }
                isDesktop={ isDesktop }
                setItemID={ setItemID }
                hasSubmenuItems={ hasSubmenuItems }
                getSubmenus={ getSubmenus }
                setMaxMenuLength={ setMaxMenuLength }
                resetMenus={ resetMenus }
                getItemInReverse={ getItemInReverse }
                submenuOpen={ isSubmenuOpen( item ) }
              />
            </li>
          ) ) }
          <li className={ `menu-item mb-0 menu-item--search ${!isDesktop ? 'is-mobile-search' : 'is-desktop-search'} ` }>
            <NavSearchBar
              isDesktop={ isDesktop }
              isOpenSearch={ isOpenSearch}
              setIsOpenSearch={ setIsOpenSearch }
              searchText={ searchText }
              setSearchText={ setSearchText }
            />
          </li>
        </ul>
      </div>
    </nav>
  );
};

const renderNav = () => {
  const header = document.querySelector( 'header' );
  if ( !header ) return;

  const root = createRoot( header );
  root.render( <Nav /> );
};

export default renderNav;
